"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { TelemetryClient } from "./client";
import { trackInteractions } from "./interactions";
import type { TelemetryConfig } from "./types";

export const TelemetryContext = createContext<TelemetryClient | null>(null);

/**
 * Tracks page views on Next.js route changes.
 * Dynamically imports usePathname to avoid hard dependency on next.
 */
function RouteTracker() {
  const client = useContext(TelemetryContext);
  const pathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!client) return;

    // Try to use Next.js router for SPA navigation tracking
    let cleanup: (() => void) | undefined;

    import("next/navigation")
      .then((mod) => {
        // For Next.js apps: listen to popstate + intercept pushState
        const origPush = history.pushState.bind(history);
        const origReplace = history.replaceState.bind(history);

        const trackNav = () => {
          const path = location.pathname;
          if (path !== pathnameRef.current) {
            pathnameRef.current = path;
            // Defer to let title update
            requestAnimationFrame(() => {
              client.track("page_view", {
                path,
                title: document.title,
              });
            });
          }
        };

        history.pushState = (...args) => {
          origPush(...args);
          trackNav();
        };
        history.replaceState = (...args) => {
          origReplace(...args);
          trackNav();
        };

        window.addEventListener("popstate", trackNav);

        cleanup = () => {
          history.pushState = origPush;
          history.replaceState = origReplace;
          window.removeEventListener("popstate", trackNav);
        };
      })
      .catch(() => {
        // Not a Next.js app — fall back to popstate only
        const trackNav = () => {
          const path = location.pathname;
          if (path !== pathnameRef.current) {
            pathnameRef.current = path;
            client.track("page_view", {
              path,
              title: document.title,
            });
          }
        };

        window.addEventListener("popstate", trackNav);
        cleanup = () => window.removeEventListener("popstate", trackNav);
      });

    return () => cleanup?.();
  }, [client]);

  return null;
}

interface TelemetryProviderProps extends Omit<TelemetryConfig, "appName"> {
  appName: string;
  children: ReactNode;
}

export function TelemetryProvider({
  children,
  appName,
  endpoint,
  enabled,
  debug,
  flushInterval,
  flushThreshold,
}: TelemetryProviderProps) {
  const clientRef = useRef<TelemetryClient | null>(null);

  if (!clientRef.current) {
    clientRef.current = new TelemetryClient({
      appName,
      endpoint,
      enabled,
      debug,
      flushInterval,
      flushThreshold,
    });
  }

  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;

    // Web vitals migrated to @workspace/observability/browser (tdd-0030).
    // Track interactions (returns cleanup function)
    const cleanupInteractions = trackInteractions(client);

    // Track initial page view
    client.track("page_view", {
      path: location.pathname,
      title: document.title,
    });

    return () => {
      cleanupInteractions();
      client.destroy();
    };
  }, []);

  return (
    <TelemetryContext value={clientRef.current}>
      <RouteTracker />
      {children}
    </TelemetryContext>
  );
}
